import { useEffect, useState } from "react";
import { useStoreManagementStore } from "@/store/storeManagement";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { AddEmployeeDialog } from "./AddEmployeeDialog";
import { useToast } from "@/hooks/use-toast";
import { StoreEmployee } from "@/models/storeModel";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";
import { EmployeeTable } from "./employees/EmployeeTable";
import { NoEmployeesFound } from "./employees/NoEmployeesFound";
import { EmployeesSkeleton } from "./employees/EmployeesSkeleton";
import { InvitationsList } from "./employees/InvitationsList";

interface StoreEmployeesProps {
  storeId: string;
  isAdminHQ: boolean;
  isStoreAdmin: boolean;
}

export function StoreEmployees({
  storeId,
  isAdminHQ,
  isStoreAdmin,
}: StoreEmployeesProps) {
  const {
    employees,
    fetchEmployees,
    updateEmployeeRole,
    removeEmployee,
    stores,
    loading,
    invitations,
    fetchInvitations,
  } = useStoreManagementStore();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<StoreEmployee | null>(null);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (storeId) {
      console.log("Fetching employees for store ID:", storeId);

      // Load both employees and invitations when component mounts
      Promise.all([
        fetchEmployees(storeId),
        fetchInvitations(storeId).catch((error) => {
          console.error("Error fetching invitations:", error);
          return [];
        }),
      ]).catch((error) => {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch employees or invitations",
          variant: "destructive",
        });
      });
    }
  }, [storeId, fetchEmployees, fetchInvitations, toast]);

  const handleRoleChange = async (
    employeeId: string,
    role: "admin" | "manager" | "staff" | "super_admin"
  ): Promise<void> => {
    try {
      // Call updateEmployeeRole but don't return its result
      await updateEmployeeRole(employeeId, role);
      // The function signature now correctly returns void
      return;
    } catch (error) {
      console.error("Error updating role:", error);
      throw error; // Re-throw to be caught by the calling function
    }
  };

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;

    try {
      await removeEmployee(employeeToDelete.id);
      toast({
        title: "Employee removed",
        description: "The employee has been removed successfully.",
      });
      setEmployeeToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove the employee.",
        variant: "destructive",
      });
    }
  };

  // Find the store name
  const storeName =
    stores.find((store) => store.id === storeId)?.name || "Unknown Store";

  // Filter employees for this store
  const storeEmployees = employees.filter((emp) => emp.storeId === storeId);
  console.log("Store employees filtered:", storeEmployees);

  // Filter invitations for this store - make sure we check if store_id exists in the type
  const storeInvitations = invitations.filter(
    (inv) => inv.store_id && inv.store_id === storeId && !inv.accepted
  );

  const canManageEmployees = isAdminHQ || isStoreAdmin;

  if (loading) {
    return <EmployeesSkeleton />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Employees - {storeName}</h2>

        {canManageEmployees && (
          <Button onClick={() => setShowAddDialog(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        )}
      </div>

      <Card>
        {storeEmployees.length === 0 ? (
          <NoEmployeesFound
            canAddEmployees={canManageEmployees}
            onAddEmployee={() => setShowAddDialog(true)}
          />
        ) : (
          <EmployeeTable
            employees={storeEmployees}
            canManageEmployees={canManageEmployees}
            onRoleChange={handleRoleChange}
            onDeleteEmployee={setEmployeeToDelete}
          />
        )}
      </Card>

      {/* Show invitations list if there are any pending invitations */}
      <InvitationsList
        invitations={storeInvitations}
        loading={isLoadingInvitations}
      />

      {showAddDialog && (
        <AddEmployeeDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          storeId={storeId}
        />
      )}

      {employeeToDelete && (
        <ConfirmDeleteDialog
          open={!!employeeToDelete}
          onOpenChange={(isOpen) => !isOpen && setEmployeeToDelete(null)}
          onConfirm={handleDeleteEmployee}
          title="Remove Employee"
          description="Are you sure you want to remove this employee from the store? This action cannot be undone."
        />
      )}
    </div>
  );
}
